import { Checkbox, FormControlLabel, Grid } from "@mui/material";
import { FormikProps } from "formik";
import CustomTextArea from "../../../../../../../../../components/CustomTextArea";
import Flex from "../../../../../../../../../components/Flex";
import Text from "../../../../../../../../../components/Text";
import CustomTitle from "../../../../../../../../../components/app/CustomTitle";
import { Form2Payload } from "../../../../../../scheduling/appointment/encounter/encounter-constant";
import CustomSelect from "../../../../../../../../../components/CustomSelect";
import CptCodesField from "../components/form-components/CptCodesField";
import IcdCodesField from "../components/form-components/IcdCodesField";

const PRIMARY_TOPIC_OPTIONS = [
  { value: "Depression Education", label: "Depression Education" },
  { value: "Anxiety Management", label: "Anxiety Management" },
  { value: "Stress Management", label: "Stress Management" },
  { value: "Coping Skills Development", label: "Coping Skills Development" },
  { value: "Emotion Regulation", label: "Emotion Regulation" },
  { value: "Communication Skills", label: "Communication Skills" },
  { value: "Healthy Relationships", label: "Healthy Relationships" },
  { value: "Setting Boundaries", label: "Setting Boundaries" },
  { value: "Self-Care Strategies", label: "Self-Care Strategies" },
  { value: "Sleep Hygiene", label: "Sleep Hygiene" },
  { value: "Nutrition & Mental Health", label: "Nutrition & Mental Health" },
  { value: "Substance Use Education", label: "Substance Use Education" },
  { value: "Trauma Education", label: "Trauma Education" },
  { value: "Grief & Loss", label: "Grief & Loss" },
  { value: "ADHD Education", label: "ADHD Education" },
  { value: "Bipolar Disorder Education", label: "Bipolar Disorder Education" },
];

const SKILLS_CATEGORIES_ITEMS = [
  { value: "Breathing Techniques", label: "Breathing Techniques" },
  { value: "Mindfulness Practices", label: "Mindfulness Practices" },
  { value: "Cognitive Restructuring", label: "Cognitive Restructuring" },
  { value: "Problem-Solving Skills", label: "Problem-Solving Skills" },
  { value: "Relaxation Techniques", label: "Relaxation Techniques" },
  { value: "Grounding Exercises", label: "Grounding Exercises" },
  { value: "Communication Skills", label: "Communication Skills" },
  { value: "Assertiveness Training", label: "Assertiveness Training" },
] as const;

const MATERIALS_DISTRIBUTED_ITEMS = [
  { value: "Education Handouts", label: "Education Handouts" },
  { value: "Practice Worksheets", label: "Practice Worksheets" },
  { value: "Video Resources", label: "Video Resources" },
  { value: "Mobile Apps/Tools", label: "Mobile Apps/Tools" },
  { value: "Book Recommendations", label: "Book Recommendations" },
  { value: "Website Resources", label: "Website Resources" },
] as const;

const ENGAGEMENT_LEVEL_OPTIONS = [
  {
    value: "Excellent",
    label: "Excellent - Highly engaged, active participation",
  },
  {
    value: "Good",
    label: "Good - Engaged, participated appropriately",
  },
  {
    value: "Moderate",
    label: "Moderate - Some engagement, limited participation",
  },
  {
    value: "Minimal",
    label: "Minimal - Low engagement, passive attendance",
  },
];

const COMPREHENSION_LEVEL_OPTIONS = [
  {
    value: "Excellent",
    label: "Excellent - Demonstrated clear understanding",
  },
  {
    value: "Good",
    label: "Good - Understood most concepts",
  },
  {
    value: "Moderate",
    label: "Moderate - Understood some concepts, needed clarification",
  },
  {
    value: "Limited",
    label: "Limited - Required significant explanation and support",
  },
];

interface PsychoeducationTherapyFormProps {
  formik: FormikProps<Form2Payload>;
  /**
   * When true, render ICD/CPT code pickers inside this form. Use `true` only
   * when this form is mounted as the standalone PSYCHOEDUCATION_THERAPY visit
   * note; parent forms that embed it already provide their own code pickers.
   */
  showCodes?: boolean;
}

const PsychoeducationTherapyForm = ({
  formik,
  showCodes = false,
}: PsychoeducationTherapyFormProps) => {
  const handleCheckboxChange = (
    fieldName: string,
    itemValue: string,
    checked: boolean
  ) => {
    const currentStr = (formik.values as any)?.[fieldName] ?? "";
    const checkedKeys = currentStr
      ? currentStr
          .split(",")
          .map((s: string) => s.trim())
          .filter(Boolean)
      : [];
    const newKeys = checked
      ? [...checkedKeys, itemValue]
      : checkedKeys.filter((k: string) => k !== itemValue);
    formik.setFieldValue(fieldName, newKeys.join(","));
  };

  const isCheckboxChecked = (fieldName: string, itemValue: string) => {
    const currentStr = (formik.values as any)?.[fieldName] ?? "";
    const checkedKeys = currentStr
      ? currentStr
          .split(",")
          .map((s: string) => s.trim())
          .filter(Boolean)
      : [];
    return checkedKeys.includes(itemValue);
  };

  return (
    <Flex p={1.5}>
      <Grid container spacing={2}>
        {/* Educational Topic & Content */}
        <Flex flexDirection="column" gap={1} ml={2} width="100%">
          <CustomTitle>Educational Topic & Content</CustomTitle>
        </Flex>
        <Grid item xs={12}>
          <Text mb={1}>Primary Topic</Text>
          <CustomSelect
            name="primaryTopic"
            value={formik.values?.primaryTopic ?? ""}
            options={PRIMARY_TOPIC_OPTIONS}
            placeholder="Select Primary Topic"
            onChange={(v) => formik.setFieldValue("primaryTopic", v)}
          />
        </Grid>
        <Grid item xs={12}>
          <Text mb={1}>Learning Objectives</Text>
          <CustomTextArea
            rows={3}
            name="learningObjectives"
            placeholder="List the specific learning objectives for this session (e.g., Understand the symptoms of depression, Learn three coping strategies for anxiety)..."
            onBlur={formik.handleBlur}
            value={formik.values?.learningObjectives ?? ""}
            onChange={formik.handleChange}
          />
        </Grid>
        <Grid item xs={12}>
          <Text mb={1}>Content Delivered</Text>
          <CustomTextArea
            rows={3}
            name="contentDelivered"
            placeholder="Document the educational content and information provided during the session, including key concepts, facts, research findings, and theoretical frameworks discussed..."
            onBlur={formik.handleBlur}
            value={formik.values?.contentDelivered ?? ""}
            onChange={formik.handleChange}
          />
        </Grid>

        {/* Skills & Strategies Taught */}
        <Flex flexDirection="column" gap={1} ml={2} width="100%" mt={2}>
          <CustomTitle>Skills & Strategies Taught</CustomTitle>
          <Text mb={1}>Skills Categories Covered</Text>
        </Flex>
        <Grid item xs={12}>
          <Grid container spacing={1}>
            {[
              SKILLS_CATEGORIES_ITEMS.slice(0, 4),
              SKILLS_CATEGORIES_ITEMS.slice(4, 8),
            ].map((column, colIndex) => (
              <Grid item xs={12} md={6} key={colIndex}>
                <Flex flexDirection="column" gap={0.5}>
                  {column.map((item) => (
                    <FormControlLabel
                      key={item.value}
                      control={
                        <Checkbox
                          checked={isCheckboxChecked(
                            "skillsCategoriesCovered",
                            item.value
                          )}
                          onChange={(e) =>
                            handleCheckboxChange(
                              "skillsCategoriesCovered",
                              item.value,
                              e.target.checked
                            )
                          }
                        />
                      }
                      label={<Text>{item.label}</Text>}
                    />
                  ))}
                </Flex>
              </Grid>
            ))}
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Text mb={1}>Skill & Strategies Description</Text>
          <CustomTextArea
            rows={3}
            name="skillsDescription"
            placeholder="Describe the skills and strategies taught in this session"
            onBlur={formik.handleBlur}
            value={formik.values?.skillsDescription ?? ""}
            onChange={formik.handleChange}
          />
        </Grid>

        {/* Materials & Resources Provided */}
        <Flex flexDirection="column" gap={1} ml={2} width="100%" mt={2}>
          <CustomTitle>Materials & Resources Provided</CustomTitle>
          <Text mb={1}>Materials Distributed</Text>
        </Flex>
        <Grid item xs={12}>
          <Grid container spacing={1}>
            {[
              MATERIALS_DISTRIBUTED_ITEMS.slice(0, 3),
              MATERIALS_DISTRIBUTED_ITEMS.slice(3, 6),
            ].map((column, colIndex) => (
              <Grid item xs={12} md={6} key={colIndex}>
                <Flex flexDirection="column" gap={0.5}>
                  {column.map((item) => (
                    <FormControlLabel
                      key={item.value}
                      control={
                        <Checkbox
                          checked={isCheckboxChecked(
                            "materialsDistributed",
                            item.value
                          )}
                          onChange={(e) =>
                            handleCheckboxChange(
                              "materialsDistributed",
                              item.value,
                              e.target.checked
                            )
                          }
                        />
                      }
                      label={<Text>{item.label}</Text>}
                    />
                  ))}
                </Flex>
              </Grid>
            ))}
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Text mb={1}>Material Details</Text>
          <CustomTextArea
            rows={3}
            name="materialDetails"
            placeholder="List specific materials, resources, handouts, or tools provided"
            onBlur={formik.handleBlur}
            value={formik.values?.materialDetails ?? ""}
            onChange={formik.handleChange}
          />
        </Grid>

        {/* Participant Engagement & Response */}
        <Flex flexDirection="column" gap={1} ml={2} width="100%" mt={2}>
          <CustomTitle>Participant Engagement & Response</CustomTitle>
        </Flex>
        <Grid item xs={12} md={6}>
          <Text mb={1}>Overall Engagement Level</Text>
          <CustomSelect
            name="engagementLevel"
            value={formik.values?.engagementLevel ?? ""}
            options={ENGAGEMENT_LEVEL_OPTIONS}
            placeholder="Select Engagement Level"
            onChange={(v) => formik.setFieldValue("engagementLevel", v)}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Text mb={1}>Comprehension & Understanding</Text>
          <CustomSelect
            name="comprehensionLevel"
            value={formik.values?.comprehensionLevel ?? ""}
            options={COMPREHENSION_LEVEL_OPTIONS}
            placeholder="Select Comprehension Level"
            onChange={(v) => formik.setFieldValue("comprehensionLevel", v)}
          />
        </Grid>
        <Grid item xs={12}>
          <Text mb={1}>Questions Asked & Discussion</Text>
          <CustomTextArea
            rows={3}
            name="questionsDiscussion"
            placeholder="Document questions asked by participant(s), topics of discussion, concerns raised, and responses provided..."
            onBlur={formik.handleBlur}
            value={formik.values?.questionsDiscussion ?? ""}
            onChange={formik.handleChange}
          />
        </Grid>
        <Grid item xs={12}>
          <Text mb={1}>Participant Feedback</Text>
          <CustomTextArea
            rows={3}
            name="participantFeedback"
            placeholder="Document verbal or written feedback from participants about the session, including what was helpful, requests for additional information, or suggested topics..."
            onBlur={formik.handleBlur}
            value={formik.values?.participantFeedback ?? ""}
            onChange={formik.handleChange}
          />
        </Grid>

        {/* Practice & Application */}
        <Flex flexDirection="column" gap={1} ml={2} width="100%" mt={2}>
          <CustomTitle>Practice & Application</CustomTitle>
        </Flex>
        <Grid item xs={12}>
          <Text mb={1}>In-Session Practice</Text>
          <CustomTextArea
            rows={3}
            name="inSessionPractice"
            placeholder="Describe any skills practiced during the session, role-plays conducted, exercises completed, or demonstrations performed..."
            onBlur={formik.handleBlur}
            value={formik.values?.inSessionPractice ?? ""}
            onChange={formik.handleChange}
          />
        </Grid>
        <Grid item xs={12}>
          <Text mb={1}>Home Practice Assignments</Text>
          <CustomTextArea
            rows={3}
            name="homePracticeAssignments"
            placeholder="List any homework, practice exercises, self-monitoring tasks, or skill application activities assigned for completion between sessions..."
            onBlur={formik.handleBlur}
            value={formik.values?.homePracticeAssignments ?? ""}
            onChange={formik.handleChange}
          />
        </Grid>
        <Grid item xs={12}>
          <Text mb={1}>Real-Life Application Plan</Text>
          <CustomTextArea
            rows={3}
            name="realLifeApplicationPlan"
            placeholder="Describe how participant(s) plan to apply learned information and skills to their daily life, including specific situations or scenarios discussed..."
            onBlur={formik.handleBlur}
            value={formik.values?.realLifeApplicationPlan ?? ""}
            onChange={formik.handleChange}
          />
        </Grid>

        {/* Follow-Up & Recommendations */}
        <Flex flexDirection="column" gap={1} ml={2} width="100%" mt={2}>
          <CustomTitle>Follow-Up & Recommendations</CustomTitle>
        </Flex>
        <Grid item xs={12}>
          <Text mb={1}>Recommended Follow-Up Topics</Text>
          <CustomTextArea
            rows={3}
            name="followUpTopics"
            placeholder="List topics for future psychoeducation sessions based on participant needs, questions, or interests identified during this session..."
            onBlur={formik.handleBlur}
            value={formik.values?.followUpTopics ?? ""}
            onChange={formik.handleChange}
          />
        </Grid>
        <Grid item xs={12}>
          <Text mb={1}>Additional Resources Needed</Text>
          <CustomTextArea
            rows={3}
            name="additionalResourcesNeeded"
            placeholder="Note any additional educational materials, resources, or referrals that would benefit the participant(s)..."
            onBlur={formik.handleBlur}
            value={formik.values?.additionalResourcesNeeded ?? ""}
            onChange={formik.handleChange}
          />
        </Grid>
        <Grid item xs={12}>
          <Text mb={1}>Next Session Plan</Text>
          <CustomTextArea
            rows={3}
            name="nextSessionPlan"
            placeholder="Outline the plan for the next psychoeducation session, including topics to be covered and any preparation needed..."
            onBlur={formik.handleBlur}
            value={formik.values?.nextSessionPlan ?? ""}
            onChange={formik.handleChange}
          />
        </Grid>

        {/* Additional Clinical Notes */}
        <Flex flexDirection="column" gap={1} ml={2} width="100%" mt={2}>
          <CustomTitle>Additional Clinical Notes</CustomTitle>
        </Flex>
        <Grid item xs={12}>
          <Text mb={1}>Provider Notes</Text>
          <CustomTextArea
            rows={3}
            name="psychoeducationProviderNote"
            placeholder="Document any additional clinical observations, safety concerns, barriers to learning identified, or other relevant information..."
            onBlur={formik.handleBlur}
            value={formik.values?.psychoeducationProviderNote ?? ""}
            onChange={formik.handleChange}
          />
        </Grid>

        {showCodes && (
          <>
            <Flex flexDirection="column" gap={1} ml={2} width="100%" mt={2}>
              <CustomTitle>Clinical Diagnoses</CustomTitle>
            </Flex>
            <Grid item xs={12}>
              <Flex flexDirection="row" gap={2} flexWrap="wrap">
                <IcdCodesField formik={formik} />
                <CptCodesField formik={formik} />
              </Flex>
            </Grid>
          </>
        )}
      </Grid>
    </Flex>
  );
};

export default PsychoeducationTherapyForm;
